import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/features/auth";
import { ROLES } from "@/features/auth/types";
import { roleNavigationConfig } from "@/config/roleNavigation.config";
import {
  Shield,
  Settings,
  Users,
  FileCheck,
  Target,
  Briefcase
} from "lucide-react";

interface ProfileOption {
  value: string;
  label: string;
  description: string;
  shortLabel?: string; // Label abreviado para telas pequenas
  icon: React.ComponentType<{ className?: string }>;
}

export const useProfileSwitcher = () => {
  const { state: { user } } = useAuth();

  // Chave para localStorage baseada no ID do usuário
  const getStorageKey = useCallback(() => `profile_switcher_${user?.id || 'anonymous'}`, [user?.id]);

  // Ref para controlar se o perfil já foi inicializado
  const initializedRef = useRef(false);

  const [selectedProfile, setSelectedProfile] = useState<string>(() => {
    // LÓGICA SIMPLES E DIRETA: sempre retornar o valor correto baseado no usuário atual
    if (!user) {
      return '';
    }

    if (user.role === ROLES.SUPER_ADMIN) {
      // Para SUPER_ADMIN: sempre verificar localStorage primeiro
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem(getStorageKey());
        if (stored) {
          return stored;
        }
      }
      return ROLES.SUPER_ADMIN;
    }

    // Para outros usuários: usar o role diretamente
    return user.role;
  });

  // Estado para forçar re-renderização quando o perfil mudar
  const [profileChangeTrigger, setProfileChangeTrigger] = useState(0);

  // O `user` chega de forma assíncrona (AuthProvider valida a sessão).
  // Se este hook montar antes de `user` estar disponível, o inicializador
  // do useState acima já rodou com `user` nulo e `selectedProfile` fica ''
  // para sempre — a troca de perfil parece não "colar" após o reload.
  // Este efeito re-sincroniza assim que o usuário estiver disponível.
  useEffect(() => {
    if (!user || initializedRef.current) return;

    if (user.role === ROLES.SUPER_ADMIN) {
      const stored = typeof window !== 'undefined' ? localStorage.getItem(getStorageKey()) : null;
      setSelectedProfile(stored || ROLES.SUPER_ADMIN);
    } else {
      setSelectedProfile(user.role);
    }

    initializedRef.current = true;
  }, [user, getStorageKey]);

  // PROTEÇÃO CONTRA BUG: corrigir automaticamente se SUPER_ADMIN estiver com LIDER_VENDAS
  useEffect(() => {
    if (!user || !selectedProfile) return;

    // BUG DETECTADO: SuperAdmin nunca deve ter LIDER_VENDAS como perfil
    if (user.role === ROLES.SUPER_ADMIN && selectedProfile === ROLES.LIDER_VENDAS) {
      console.warn('[useProfileSwitcher] Corrigindo perfil inválido para SuperAdmin');

      const stored = localStorage.getItem(getStorageKey());
      const correctProfile = stored || ROLES.SUPER_ADMIN;

      setSelectedProfile(correctProfile);
      setProfileChangeTrigger(prev => prev + 1);
    }
  }, [user, selectedProfile, getStorageKey]);


  // Opções de perfis disponíveis para o Super Admin (e labels do card de perfil)
  const profileOptions: ProfileOption[] = [
    {
      value: ROLES.SUPER_ADMIN,
      label: "Super Admin",
      shortLabel: "Super Admin",
      description: "Acesso completo ao sistema",
      icon: Shield
    },
    {
      value: ROLES.REPRESENTANTE,
      label: "Representante",
      shortLabel: "Representante",
      description: "Administração da representação",
      icon: Briefcase
    },
    {
      value: ROLES.ASSOCIADO,
      label: "Associado",
      shortLabel: "Admin Geral",
      description: "Gestão completa da empresa",
      icon: Settings
    },
    {
      value: ROLES.SUPERVISOR,
      label: "Supervisor",
      shortLabel: "Supervisor",
      description: "Supervisão e gestão de equipe",
      icon: Users
    },
    {
      value: ROLES.SECRETARIA,
      label: "Secretária",
      shortLabel: "Secretária",
      description: "Visualização geral de todos os dados",
      icon: FileCheck
    },
    {
      value: ROLES.RELACIONADOR,
      label: "Relacionador",
      shortLabel: "Relacionador",
      description: "Prospecção e qualificação",
      icon: Target
    },
    {
      value: ROLES.LIDER_VENDAS,
      label: "Líder de Vendas",
      shortLabel: "Líder Vendas",
      description: "Finalização e fechamento",
      icon: Briefcase
    },
    {
      value: ROLES.POS_VENDA,
      label: "Pós-Venda",
      shortLabel: "Pós-Venda",
      description: "Acompanhamento pós-venda",
      icon: FileCheck
    },
    {
      value: ROLES.USER,
      label: "Consultor",
      shortLabel: "Consultor",
      description: "Acesso operacional",
      icon: Users
    },
  ];

  // Verificar se o usuário é Super Admin
  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;

  // Salvar no localStorage sempre que o selectedProfile mudar
  useEffect(() => {
    if (typeof window !== 'undefined' && user?.id && isSuperAdmin) {
      localStorage.setItem(getStorageKey(), selectedProfile);
    }
  }, [selectedProfile, user?.id, isSuperAdmin, getStorageKey]);

  // Obter navegação baseada no perfil selecionado
  const getNavigationForProfile = useCallback((profile: string) => {
    return roleNavigationConfig[profile as keyof typeof roleNavigationConfig] || roleNavigationConfig[ROLES.LIDER_VENDAS];
  }, []);

  // Mapear roles antigos para novos roles
  const mapOldRoleToNew = useCallback((role: string) => {
    const roleMapping: Record<string, string> = {
      'ADMIN': ROLES.ASSOCIADO,
      'VENDEDOR': ROLES.LIDER_VENDAS,
      'CONSULTOR': ROLES.RELACIONADOR,
      'SUPERVISOR': ROLES.SUPERVISOR,
      'RELACIONADOR': ROLES.RELACIONADOR,
      'SECRETARIA': ROLES.SECRETARIA,
      'LIDER_VENDAS': ROLES.LIDER_VENDAS,
      'REPRESENTANTE': ROLES.REPRESENTANTE,
      'ASSOCIADO': ROLES.ASSOCIADO,
      'POS_VENDA': ROLES.POS_VENDA,
      'USER': ROLES.USER,
      'SUPER_ADMIN': ROLES.SUPER_ADMIN,
    };
    return roleMapping[role] || role;
  }, []);

  // Obter perfil atual (real ou simulado) - memoizado para evitar re-cálculos
  const getCurrentProfile = useMemo(() => {
    if (!user) {
      return ROLES.USER;
    }

    // Se o selectedProfile ainda não foi inicializado, usar o role do usuário
    if (!selectedProfile) {
      return mapOldRoleToNew(user.role);
    }

    const mappedRole = mapOldRoleToNew(user.role);
    return isSuperAdmin ? selectedProfile : mappedRole;
  }, [isSuperAdmin, selectedProfile, user?.role, mapOldRoleToNew]);

  // Obter navegação atual - memoizado
  const getCurrentNavigation = useMemo(() => {
    return getNavigationForProfile(getCurrentProfile);
  }, [getCurrentProfile, getNavigationForProfile]);

  // Obter informações do perfil atual - memoizado
  const getCurrentProfileInfo = useMemo(() => {
    const currentProfile = getCurrentProfile;
    const foundOption = profileOptions.find(option => option.value === currentProfile);

    if (foundOption) {
      return foundOption;
    }

    // Fallback: monta label a partir do role real, sem forçar Líder de Vendas
    const mappedRole = user?.role ? mapOldRoleToNew(user.role) : ROLES.USER;
    const fallbackOption = profileOptions.find(option => option.value === mappedRole);

    if (fallbackOption) {
      return fallbackOption;
    }

    return {
      value: mappedRole,
      label: mappedRole.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      shortLabel: mappedRole,
      description: '',
      icon: Users,
    } satisfies ProfileOption;
  }, [getCurrentProfile, user?.role, mapOldRoleToNew, profileOptions]);

  // Mudar perfil (apenas para Super Admin)
  const switchProfile = useCallback((newProfile: string) => {
    if (!user) {
      console.warn('Usuário não disponível para mudança de perfil');
      return;
    }

    if (isSuperAdmin) {
      setSelectedProfile(newProfile);
      // Forçar re-renderização imediata
      setProfileChangeTrigger(prev => prev + 1);

      // Atualizar a página após um pequeno delay para garantir que o estado seja salvo
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }, [isSuperAdmin, user]);

  // Resetar para perfil real
  const resetToRealProfile = useCallback(() => {
    if (!user) {
      console.warn('Usuário não disponível para reset de perfil');
      return;
    }

    if (isSuperAdmin) {
      const realRole = user?.role || ROLES.LIDER_VENDAS;
      setSelectedProfile(realRole);
      // Salvar no localStorage
      if (typeof window !== 'undefined' && user?.id) {
        localStorage.setItem(getStorageKey(), realRole);
      }
      // Forçar re-renderização imediata
      setProfileChangeTrigger(prev => prev + 1);

      // Atualizar a página após um pequeno delay para garantir que o estado seja salvo
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }, [isSuperAdmin, user?.role, getStorageKey, user]);

  // Verificar se está simulando outro perfil
  const isSimulating = isSuperAdmin && selectedProfile !== user?.role;

  // Função para limpar o localStorage (útil para logout)
  const clearStoredProfile = useCallback(() => {
    if (typeof window !== 'undefined' && user?.id) {
      localStorage.removeItem(getStorageKey());
      console.log('🗑️ Profile cleared from localStorage:', { userId: user.id });
    }
  }, [user?.id, getStorageKey]);

  // Função para obter o label apropriado baseado no contexto
  const getDisplayLabel = useCallback((profile: ProfileOption, isCollapsed: boolean = false) => {
    if (isCollapsed && profile.shortLabel) {
      return profile.shortLabel;
    }
    return profile.label;
  }, []);

  return {
    // Estado
    selectedProfile,
    profileOptions,
    isSuperAdmin,
    isSimulating,
    profileChangeTrigger, // Expor o trigger para forçar re-renderização

    // Ações
    switchProfile,
    resetToRealProfile,
    clearStoredProfile,

    // Dados
    getCurrentProfile,
    getCurrentNavigation,
    getCurrentProfileInfo,
    getDisplayLabel, // Nova função para labels responsivos
  };
};